import type {
  FixedPhoneAccountDetailsData,
  FixedPhoneAccountDetailsNormalized,
  FixedPhoneAccountDetailsResponse,
  FixedPhoneAccountDetailsSubscriptionRow,
  FixedPhoneCustomerDetails,
  FixedPhoneDashboardData,
  FixedPhoneDashboardPayload,
  FixedPhoneDashboardResponse,
} from "./types";

/** Picks a subscription id from arbitrary API object shapes. */
export function resolveSubscriptionRecordId(
  raw: Record<string, unknown>
): string | undefined {
  for (const c of [
    raw.subscriptionId,
    raw.subscription_id,
    raw.id,
    raw._id,
  ]) {
    if (typeof c === "string" && c.trim()) {
      return c.trim();
    }
  }
  return undefined;
}

/**
 * Picks a stable home-phone subscription id from dashboard payload or raw API
 * (camelCase, snake_case, or id-style fields from the service).
 */
export function resolveFixedPhoneSubscriptionId(
  raw: FixedPhoneDashboardData & Record<string, unknown>
): string | undefined {
  return resolveSubscriptionRecordId(raw as Record<string, unknown>);
}

/** Ensures each subscription in the dashboard response exposes `subscriptionId` when the API sent it under any supported key. */
export function normalizeFixedPhoneDashboardPayload(
  payload: FixedPhoneDashboardPayload
): FixedPhoneDashboardPayload {
  if (!payload?.subscriptions?.length) {
    return payload;
  }
  return {
    ...payload,
    subscriptions: payload.subscriptions.map((sub) => {
      const id = resolveFixedPhoneSubscriptionId(
        sub as FixedPhoneDashboardData & Record<string, unknown>
      );
      if (!id) {
        return sub;
      }
      if (sub.subscriptionId === id) {
        return sub;
      }
      return { ...sub, subscriptionId: id };
    }),
  };
}

export function transformFixedPhoneDashboardResponse(
  response: FixedPhoneDashboardResponse
): FixedPhoneDashboardResponse {
  if (!response?.data) {
    return response;
  }
  return {
    ...response,
    data: normalizeFixedPhoneDashboardPayload(response.data),
  };
}

function normalizeAccountDetailsSubscriptionRow(
  customerId: string | null | undefined,
  customer: FixedPhoneCustomerDetails | null | undefined,
  row: FixedPhoneAccountDetailsSubscriptionRow
): FixedPhoneAccountDetailsData {
  const account = row.account ?? null;
  const subscription = row.subscription ?? null;
  const idFromAccount =
    typeof account?.subscriptionId === "string" && account.subscriptionId.trim()
      ? account.subscriptionId.trim()
      : undefined;
  const idFromSubscription = subscription
    ? resolveSubscriptionRecordId(subscription as unknown as Record<string, unknown>)
    : undefined;
  const mergedSubscriptionId = idFromSubscription ?? idFromAccount;

  return {
    customerId: customerId ?? null,
    customer: customer ?? null,
    account,
    e911: row.e911 ?? null,
    lineDetails: row.lineDetails ?? null,
    subscription:
      subscription || mergedSubscriptionId
        ? {
            ...(subscription ?? {}),
            ...(mergedSubscriptionId
              ? { subscriptionId: mergedSubscriptionId }
              : {}),
          }
        : null,
    plan: row.plan ?? null,
  };
}

export function transformFixedPhoneAccountDetailsResponse(
  response: FixedPhoneAccountDetailsResponse
): FixedPhoneAccountDetailsResponse {
  const data = response?.data as unknown as
    | FixedPhoneAccountDetailsNormalized
    | FixedPhoneAccountDetailsData
    | null
    | undefined;
  if (!data) {
    return response;
  }

  const asRecord = data as unknown as Record<string, unknown>;
  const customerId =
    (asRecord.customerId as string | undefined) ?? undefined;
  const customer = (asRecord.customer as FixedPhoneCustomerDetails | undefined) ?? undefined;

  let rows: FixedPhoneAccountDetailsSubscriptionRow[] = [];
  if (Array.isArray(asRecord.subscriptions)) {
    rows = asRecord.subscriptions as FixedPhoneAccountDetailsSubscriptionRow[];
  } else if (
    asRecord.account ||
    asRecord.lineDetails ||
    asRecord.subscription ||
    asRecord.plan
  ) {
    rows = [
      {
        account: asRecord.account as FixedPhoneAccountDetailsSubscriptionRow["account"],
        e911: asRecord.e911 as FixedPhoneAccountDetailsSubscriptionRow["e911"],
        lineDetails: asRecord.lineDetails as FixedPhoneAccountDetailsSubscriptionRow["lineDetails"],
        subscription: asRecord.subscription as FixedPhoneAccountDetailsSubscriptionRow["subscription"],
        plan: asRecord.plan as FixedPhoneAccountDetailsSubscriptionRow["plan"],
      },
    ];
  }

  const subscriptions = rows.map((row) =>
    normalizeAccountDetailsSubscriptionRow(customerId ?? null, customer ?? null, row)
  );

  const normalized: FixedPhoneAccountDetailsNormalized = {
    customerId: customerId ?? null,
    customer: customer ?? null,
    subscriptions,
  };

  return {
    ...response,
    data: normalized,
  };
}
